import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X, Maximize2, Minimize2, Minus, RotateCcw, Printer, Copy } from "lucide-react"
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from "@/components/ui/context-menu"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const dlgKey = (id) => (id ? `dlg:${localStorage.getItem("uid") || "u"}:${id}` : null);
const loadDlg = (id) => { try { return (dlgKey(id) && JSON.parse(localStorage.getItem(dlgKey(id)))) || {}; } catch { return {}; } };

const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const id = props["data-testid"];
  const saved = React.useMemo(() => loadDlg(id), [id]);
  const [mode, setModeRaw] = React.useState(saved.mode || "normal"); // normal | max | min
  const [size, setSizeRaw] = React.useState(saved.size || null);
  const [pos, setPosRaw] = React.useState(saved.pos || { x: 0, y: 0 });
  const persist = (patch) => { const k = dlgKey(id); if (k) localStorage.setItem(k, JSON.stringify({ mode, size, pos, ...patch })); };
  const setMode = (m) => { setModeRaw(m); persist({ mode: m }); };
  const setSize = (s) => { setSizeRaw(s); persist({ size: s }); };
  const setPos = (p) => { setPosRaw(p); persist({ pos: p }); };
  const inner = React.useRef(null);
  React.useImperativeHandle(ref, () => inner.current);
  const track = (e, onMove) => {
    e.preventDefault();
    const sx = e.clientX, sy = e.clientY;
    const move = (ev) => onMove(ev.clientX - sx, ev.clientY - sy);
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move); window.addEventListener("mouseup", up);
  };
  const onDrag = (e) => {
    const el = inner.current; if (!el) return;
    const r = el.getBoundingClientRect();
    track(e, (dx, dy) => setSize({ w: Math.max(320, r.width + dx * 2), h: Math.max(200, r.height + dy * 2) }));
  };
  const onTitleDown = (e) => {
    if (mode === "max" || e.button !== 0) return;
    if (!e.target.closest("[data-dialog-header]") || e.target.closest("button, input, select, textarea, a, [role=combobox]")) return;
    const p0 = pos;
    const r = inner.current?.getBoundingClientRect();
    const maxX = r ? Math.max(0, (window.innerWidth - r.width) / 2) : 1e9, maxY = r ? Math.max(0, (window.innerHeight - r.height) / 2) : 1e9;
    const clamp = (v, m) => Math.max(-m, Math.min(m, v));
    track(e, (dx, dy) => setPos({ x: clamp(p0.x + dx, maxX), y: clamp(p0.y + dy, maxY) }));
  };
  const style = mode === "max" ? undefined : {
    ...(mode === "normal" && size ? { width: size.w, height: size.h, maxWidth: "98vw", maxHeight: "98dvh" } : {}),
    ...((pos.x || pos.y) ? { transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px))` } : {}),
  };
  const modeCls = mode === "max" ? "!w-screen !h-dvh !max-w-none !max-h-none !rounded-none" : mode === "min" ? "!w-[420px] !max-w-[95vw] !h-auto" : "";
  const resetPos = () => { setSizeRaw(null); setPosRaw({ x: 0, y: 0 }); persist({ size: null, pos: { x: 0, y: 0 } }); };
  const closeBtn = React.useRef(null);
  return (
  <DialogPortal>
    <DialogOverlay />
    <ContextMenu>
    <ContextMenuTrigger asChild>
    <DialogPrimitive.Content
      ref={inner}
      style={style}
      onMouseDown={onTitleDown}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-[calc(100%-1rem)] max-h-[92dvh] overflow-y-auto max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-4 sm:p-6 shadow-lg rounded-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        className,
        modeCls
      )}
      {...props}>
      {children}
      <div className="absolute right-4 top-4 flex items-center gap-1 print:hidden">
        <button type="button" title={mode === "min" ? "Taille normale" : "Réduire"} onClick={() => { setSize(null); setMode(mode === "min" ? "normal" : "min"); }} className="rounded-sm p-0.5 opacity-70 hover:opacity-100" data-testid="dialog-minimize"><Minus className="h-4 w-4" /></button>
        <button type="button" title={mode === "max" ? "Restaurer" : "Agrandir (plein écran)"} onClick={() => { setSize(null); setMode(mode === "max" ? "normal" : "max"); }} className="rounded-sm p-0.5 opacity-70 hover:opacity-100" data-testid="dialog-maximize">{mode === "max" ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}</button>
        {(pos.x || pos.y || size) && mode === "normal" ? <button type="button" title="Recentrer / taille par défaut" onClick={resetPos} className="rounded-sm p-0.5 opacity-70 hover:opacity-100" data-testid="dialog-reset"><RotateCcw className="h-4 w-4" /></button> : null}
        <DialogPrimitive.Close
          ref={closeBtn}
          className="rounded-sm bg-red-600 p-0.5 text-white ring-offset-background transition-colors hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
          title="Fermer">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </div>
      {mode === "normal" && <div onMouseDown={onDrag} title="Glisser pour redimensionner" className="absolute bottom-0 right-0 h-5 w-5 cursor-nwse-resize print:hidden" data-testid="dialog-resize-handle"><svg viewBox="0 0 20 20" className="h-5 w-5 text-emerald-600"><path d="M18 8 8 18M18 13l-5 5M18 18" stroke="currentColor" strokeWidth="1.5" fill="none" /></svg></div>}
    </DialogPrimitive.Content>
    </ContextMenuTrigger>
    <ContextMenuContent className="w-56 print:hidden" data-testid="dialog-context-menu">
      <ContextMenuItem onSelect={() => { setSize(null); setMode(mode === "max" ? "normal" : "max"); }}>{mode === "max" ? <Minimize2 className="mr-2 h-4 w-4" /> : <Maximize2 className="mr-2 h-4 w-4" />}{mode === "max" ? "Restaurer la fenêtre" : "Agrandir (plein écran)"}</ContextMenuItem>
      <ContextMenuItem onSelect={() => { setSize(null); setMode(mode === "min" ? "normal" : "min"); }}><Minus className="mr-2 h-4 w-4" />{mode === "min" ? "Taille normale" : "Réduire la fenêtre"}</ContextMenuItem>
      <ContextMenuItem onSelect={resetPos}><RotateCcw className="mr-2 h-4 w-4" /> Recentrer / taille par défaut</ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem onSelect={() => setTimeout(() => window.print(), 150)}><Printer className="mr-2 h-4 w-4" /> Imprimer cette fenêtre</ContextMenuItem>
      <ContextMenuItem onSelect={() => { const el = inner.current; if (!el) return; navigator.clipboard?.writeText(el.innerText || ""); }}><Copy className="mr-2 h-4 w-4" /> Copier le contenu (texte)</ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem onSelect={() => closeBtn.current?.click()}><X className="mr-2 h-4 w-4" /> Fermer</ContextMenuItem>
    </ContextMenuContent>
    </ContextMenu>
  </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    data-dialog-header=""
    title="Glisser pour déplacer la fenêtre"
    className={cn("flex cursor-move select-none flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
